import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight, Clock, FileDown, MessageCircle, Phone, ShieldCheck } from "lucide-react";
import usePageMeta, { useJsonLd } from "@/hooks/usePageMeta";
import useProducts from "@/hooks/useProducts";
import { getProduct } from "@/lib/api";
import { useInquiry } from "@/context/InquiryContext";
import ProductCard from "@/components/ProductCard";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { btnGhostLight, btnPrimary, SITE } from "@/lib/site";

export default function ProductDetail() {
  const { slug } = useParams();
  const { openOrder, openQuote } = useInquiry();
  const { data: products } = useProducts();
  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
  });

  useJsonLd(
    product
      ? {
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.tagline,
          image: product.image,
          sku: `KSC-${product.slug.replace(/-/g, "").slice(0, 2).toUpperCase()}-100`,
          brand: { "@type": "Brand", name: "Krithish Scientific Company" },
        }
      : null
  );

  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState("50% 50%");

  usePageMeta(
    product ? `${product.name} | Krithish Scientific Company` : "Products | Krithish Scientific Company",
    product ? `${product.tagline} Full specifications, application areas and online ordering from Krithish Scientific Company.` : "Laboratory equipment catalog."
  );

  if (isLoading)
    return (
      <div className="bg-white pt-[104px] lg:pt-[112px]">
        <div className="mx-auto max-w-7xl px-6 py-24">
          <div className="h-96 animate-pulse rounded-2xl bg-slate-200/60" />
        </div>
      </div>
    );

  if (isError || !product)
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center bg-slate-50 px-6 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-safety">404</p>
        <h1 className="mt-3 font-display text-3xl font-bold text-slate-900">Product not found</h1>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 rounded-lg bg-safety px-6 py-3 text-sm font-semibold text-white hover:bg-safety-dark">
          Back to all products
        </Link>
      </div>
    );

  const related = (products || []).filter((p) => p.slug !== slug).slice(0, 4);
  const modelCode = `KSC-${product.slug.replace(/-/g, "").slice(0, 2).toUpperCase()}-100`;

  return (
    <div className="bg-white" data-testid="product-detail">
      {/* Breadcrumb */}
      <div className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto flex max-w-7xl items-center gap-2 px-6 py-4 font-mono text-[11px] uppercase tracking-wider text-slate-500">
          <Link to="/" className="transition hover:text-safety">Home</Link>
          <ChevronRight size={11} />
          <Link to="/products" className="transition hover:text-safety">Products</Link>
          <ChevronRight size={11} />
          <span className="text-slate-900">{product.name}</span>
        </div>
      </div>

      {/* Top */}
      <section className="py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-12 lg:grid-cols-12">
            {/* Gallery */}
            <div className="lg:col-span-7">
              <FadeUp>
                <div
                  className="relative aspect-[4/3] cursor-zoom-in overflow-hidden rounded-2xl border border-slate-200 bg-slate-100"
                  onMouseEnter={() => setZoom(true)}
                  onMouseLeave={() => setZoom(false)}
                  onMouseMove={(e) => {
                    const r = e.currentTarget.getBoundingClientRect();
                    setOrigin(`${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}% ${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
                  }}
                  data-testid="product-image-spotlight"
                >
                  <img
                    src={product.gallery[activeImg]}
                    alt={product.name}
                    className="h-full w-full object-cover transition-transform duration-300"
                    style={{ transformOrigin: origin, transform: zoom ? "scale(1.7)" : "scale(1)" }}
                  />
                  <span className="absolute left-4 top-4 rounded bg-navy/85 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-sky-300">
                    {modelCode}
                  </span>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  {product.gallery.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      data-testid={`product-thumb-${i}`}
                      className={`aspect-[4/3] overflow-hidden rounded-xl border-2 transition ${activeImg === i ? "border-safety" : "border-transparent opacity-70 hover:opacity-100"}`}
                    >
                      <img src={img} alt={`${product.name} view ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              </FadeUp>

              {/* Description */}
              <FadeUp delay={0.08}>
                <h2 className="mt-14 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">Overview</h2>
                <div className="mt-4 space-y-4 text-base leading-relaxed text-slate-600">
                  {product.description.map((d, i) => (
                    <p key={i}>{d}</p>
                  ))}
                </div>
              </FadeUp>

              {/* Specs */}
              {product.specs?.length > 0 && (
                <FadeUp delay={0.1}>
                  <h2 className="mt-14 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">
                    Technical Specifications
                  </h2>
                  <table className="mt-4 w-full text-sm" data-testid="product-specs-table">
                    <tbody>
                      {product.specs.map((s, i) => (
                        <tr key={s.label} className={`border-b border-slate-100 ${i % 2 === 0 ? "bg-slate-50/60" : ""}`}>
                          <th className="w-2/5 py-3.5 pl-4 pr-6 text-left font-mono text-[11px] font-medium uppercase tracking-wider text-slate-500">
                            {s.label}
                          </th>
                          <td className="py-3.5 pr-4 font-mono text-[13px] text-slate-900">{s.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </FadeUp>
              )}

              {/* Applications */}
              <FadeUp delay={0.12}>
                <h2 className="mt-14 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">Application Areas</h2>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="product-applications">
                  {product.applications.map((a) => (
                    <span key={a} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium text-slate-700">
                      {a}
                    </span>
                  ))}
                </div>
              </FadeUp>

              {product.variants?.length > 0 && (
                <FadeUp delay={0.14}>
                  <h2 className="mt-14 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-safety">Models & Variants</h2>
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="product-variants">
                    {product.variants.map((v) => (
                      <span key={v} className="rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-xs font-medium text-slate-800">
                        {v}
                      </span>
                    ))}
                  </div>
                </FadeUp>
              )}
            </div>

            {/* Sticky order card */}
            <div className="lg:col-span-5">
              <div className="top-32 lg:sticky" data-testid="product-order-card">
                <FadeUp delay={0.06}>
                  <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-lg shadow-slate-200/50">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-safety">Price on request</p>
                    <h1 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                      {product.name}
                    </h1>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{product.tagline}</p>

                    <div className="mt-5 flex items-center gap-2 rounded-lg bg-sky-50 px-4 py-3 text-xs text-slate-600">
                      <Clock size={14} className="shrink-0 text-safety" />
                      Quotation within one working day — no commitment needed.
                    </div>

                    <div className="mt-5 space-y-3">
                      <button
                        data-testid="place-order-button"
                        onClick={() => openOrder(product.name)}
                        className={`${btnPrimary} w-full`}
                      >
                        Place an Order
                      </button>
                      <button
                        data-testid="ask-for-price-button"
                        onClick={() => openQuote(product.name)}
                        className={`${btnGhostLight} w-full !border-slate-300 !text-slate-800 hover:!bg-slate-900 hover:!text-white`}
                      >
                        Ask for Price
                      </button>
                      <Link
                        to={`/products/${product.slug}/spec`}
                        data-testid="download-spec-btn"
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-800 transition hover:border-safety hover:text-safety"
                      >
                        <FileDown size={16} /> Spec Sheet / PDF
                      </Link>
                      <a
                        href={SITE.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="product-whatsapp-link"
                        className="flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-5 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-600 hover:text-white"
                      >
                        <MessageCircle size={16} /> Chat on WhatsApp
                      </a>
                    </div>

                    <div className="mt-6 space-y-2.5 border-t border-slate-100 pt-5 text-xs text-slate-500">
                      <p className="flex items-center gap-2">
                        <ShieldCheck size={14} className="text-safety" /> Quality-checked at our Chennai workshop before dispatch
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone size={14} className="text-safety" /> Spec help: {SITE.phoneDisplay}
                      </p>
                    </div>
                  </div>
                </FadeUp>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-slate-50 py-20" data-testid="related-products">
          <div className="mx-auto max-w-7xl px-6">
            <SectionHeading eyebrow="You May Also Need" title="Other lines from our catalog." />
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => (
                <FadeUp key={p.slug} delay={i * 0.05} className="h-full">
                  <ProductCard p={p} index={i} />
                </FadeUp>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
